import { DialogComponent } from '@theme/dialog';

/**
 * @param {boolean} isKorean - Use YYYY,MM,DD, style vs English long date.
 * @returns {string}
 */
export function formatPushConsentDate(isKorean) {
  const d = new Date();
  if (isKorean) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y},${m},${day},`;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * @returns {boolean}
 */
function isStoreLocaleKorean() {
  const lang = document.documentElement.lang || '';
  if (lang.toLowerCase().startsWith('ko')) return true;
  const shopifyLocale = window.Shopify?.locale;
  if (typeof shopifyLocale === 'string' && shopifyLocale.toLowerCase().startsWith('ko')) return true;
  return false;
}

/**
 * Shopify `| t` HTML-escapes characters like `>` to `&gt;` before `| json` in Liquid.
 * Use for plain text / textContent (not for raw HTML).
 * @param {string} text
 * @returns {string}
 */
function decodeShopifyTranslationText(text) {
  if (typeof text !== 'string' || text.length === 0) return text;
  const ta = document.createElement('textarea');
  ta.innerHTML = text;
  return ta.value;
}

/**
 * Page popup component that loads a page and shows its main content.
 *
 * The component keeps a small in-memory cache so repeated opens do not
 * refetch the same page.
 */
class PagePopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'body', 'loading', 'content', 'error'];

  #cache = new Map();
  #abortController = null;
  #loadToken = 0;
  #defaultPopupWidth = 'min(920px, calc(100vw - 24px))';
  #appDownloadPopupWidth = 'min(560px, calc(100vw - 24px))';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.#abortPendingRequest();

    super.disconnectedCallback();
  }

  /**
   * Opens the popup and loads a page into it.
   *
   * @param {string} url - Page URL to load.
   * @param {string} [title] - Title shown in the popup header.
   */
  async openPage(url, title = '') {
    const pageUrl = this.#normalizeUrl(url);
    if (!pageUrl) return;

    this.#applyDialogWidth(pageUrl);

    const loadToken = ++this.#loadToken;
    this.#abortPendingRequest();
    this.#resetState(title);

    this.showDialog();

    try {
      const html = await this.#getPageHTML(pageUrl);
      if (!html || !this.isConnected || loadToken !== this.#loadToken) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const mainContent = parsedDocument.getElementById('MainContent');

      if (!mainContent) {
        this.#showError();
        return;
      }

      const pageContent = mainContent.innerHTML.trim();
      if (!pageContent) {
        this.#showError();
        return;
      }

      this.refs.content.innerHTML = pageContent;
      this.#showContent();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Failed to load page popup content:', error);
      this.#showError();
    }
  }

  /**
   * Shows push consent / opt-out confirmation (no fetch). Copy + date from #page-popup-push-i18n.
   *
   * @param {'received' | 'rejected'} kind
   */
  openPushNotificationResult(kind) {
    if (kind !== 'received' && kind !== 'rejected') return;

    const loadToken = ++this.#loadToken;
    this.#abortPendingRequest();

    const scriptEl = document.getElementById('page-popup-push-i18n');
    if (!(scriptEl instanceof HTMLScriptElement) || !scriptEl.textContent?.trim()) {
      return;
    }

    /** @type {Record<string, string> | null} */
    let i18n;
    try {
      i18n = JSON.parse(scriptEl.textContent);
    } catch {
      console.error('page-popup: invalid page-popup-push-i18n JSON');
      return;
    }

    if (!i18n || loadToken !== this.#loadToken || !this.isConnected) return;

    for (const key of Object.keys(i18n)) {
      if (typeof i18n[key] === 'string') {
        i18n[key] = decodeShopifyTranslationText(i18n[key]);
      }
    }

    const isKo = isStoreLocaleKorean();
    const dateStr = formatPushConsentDate(isKo);
    const title = (i18n.title || '').trim();
    const bodyKey = kind === 'received' ? 'received_body' : 'rejected_body';
    const footerKey = kind === 'received' ? 'received_footer' : 'rejected_footer';
    const bodyTemplate = i18n[bodyKey] || '';
    const body = bodyTemplate.replace(/\{date\}/g, dateStr);
    const footer = i18n[footerKey] || '';
    const confirmLabel = i18n.confirm || 'CONFIRM';

    this.refs.title.textContent = title;
    this.refs.body.scrollTop = 0;
    this.refs.body.removeAttribute('aria-busy');
    this.refs.loading.hidden = true;
    this.refs.error.hidden = true;

    const dialogEl = this.refs.dialog;
    if (dialogEl instanceof HTMLElement) {
      dialogEl.classList.add('page-popup--push');
    }

    const wrap = document.createElement('div');
    wrap.className = 'page-popup__push';

    const bodyParts = body.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
    for (const text of bodyParts) {
      const p = document.createElement('p');
      p.className = 'page-popup__push-para';
      p.textContent = text;
      wrap.appendChild(p);
    }

    if (footer.trim()) {
      const fp = document.createElement('p');
      fp.className = 'page-popup__push-footer';
      fp.textContent = footer.trim();
      wrap.appendChild(fp);
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'page-popup__push-confirm';
    btn.textContent = confirmLabel;
    btn.addEventListener('click', () => {
      this.closePageDialog();
    });
    wrap.appendChild(btn);

    this.refs.content.replaceChildren(wrap);
    this.refs.content.hidden = false;

    this.showDialog();
  }

  /**
   * Closes the dialog and cancels any pending fetch.
   */
  closePageDialog = async () => {
    this.#loadToken += 1;
    this.#abortPendingRequest();
    await this.closeDialog();
  };

  /**
   * Normalizes a URL string against the current page.
   *
   * @param {string} url - The incoming URL.
   * @returns {string} The resolved URL.
   */
  #normalizeUrl(url) {
    if (!url) return '';

    try {
      return new URL(url, window.location.href).toString();
    } catch {
      return '';
    }
  }

  /**
   * Applies popup width per page URL.
   *
   * @param {string} pageUrl - The resolved page URL.
   */
  #applyDialogWidth(pageUrl) {
    let popupWidth = this.#defaultPopupWidth;

    try {
      const { pathname } = new URL(pageUrl);
      if (pathname === '/pages/app-download') {
        popupWidth = this.#appDownloadPopupWidth;
      }
    } catch {
      popupWidth = this.#defaultPopupWidth;
    }

    this.refs.dialog.style.setProperty('--page-popup-width', popupWidth);
    this.refs.dialog.style.setProperty('--custom-dialog-width', popupWidth);
  }

  /**
   * Fetches a page and caches the raw HTML by URL.
   *
   * @param {string} pageUrl - The page URL.
   * @returns {Promise<string>} The page HTML.
   */
  async #getPageHTML(pageUrl) {
    const useCache = !window.Shopify?.designMode;
    const cachedHTML = useCache ? this.#cache.get(pageUrl) : undefined;
    if (cachedHTML) return cachedHTML;

    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const response = await fetch(pageUrl, {
        signal: controller.signal,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch page popup content: HTTP ${response.status}`);
      }

      const html = await response.text();
      if (useCache) {
        this.#cache.set(pageUrl, html);
      }

      return html;
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Cancels the in-flight request, if any.
   */
  #abortPendingRequest() {
    if (!this.#abortController) return;

    this.#abortController.abort();
    this.#abortController = null;
  }

  /**
   * Invalidates the current load when the dialog closes by any path.
   */
  #handleDialogClose = () => {
    this.#loadToken += 1;
    this.#abortPendingRequest();

    const dialogEl = this.refs.dialog;
    if (dialogEl instanceof HTMLElement) {
      dialogEl.classList.remove('page-popup--push');
    }
    if (this.refs.content) {
      this.refs.content.innerHTML = '';
      this.refs.content.hidden = true;
    }
    if (this.refs.title) {
      this.refs.title.textContent = '';
    }
    if (this.refs.loading) {
      this.refs.loading.hidden = true;
    }
    if (this.refs.error) {
      this.refs.error.hidden = true;
    }
  };

  /**
   * Resets the popup state before loading a new page.
   *
   * @param {string} title - Title to show in the popup header.
   */
  #resetState(title) {
    this.refs.title.textContent = title.trim();
    this.refs.body.scrollTop = 0;
    this.refs.body.setAttribute('aria-busy', 'true');

    this.refs.loading.hidden = false;
    this.refs.content.hidden = true;
    this.refs.content.innerHTML = '';
    this.refs.error.hidden = true;
  }

  /**
   * Shows the loaded page content.
   */
  #showContent() {
    this.refs.body.removeAttribute('aria-busy');
    this.refs.loading.hidden = true;
    this.refs.error.hidden = true;
    this.refs.content.hidden = false;
  }

  /**
   * Shows the error state when the page cannot be loaded.
   */
  #showError() {
    this.refs.body.removeAttribute('aria-busy');
    this.refs.loading.hidden = true;
    this.refs.content.hidden = true;
    this.refs.error.hidden = false;
  }
}

if (!customElements.get('page-popup-component')) {
  customElements.define('page-popup-component', PagePopupComponent);
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target instanceof Element ? event.target.closest('[data-page-popup-trigger]') : null;
  if (!(target instanceof HTMLAnchorElement)) return;

  const popup = document.getElementById('page-popup');
  if (!(popup instanceof PagePopupComponent)) return;

  const url = target.getAttribute('href');
  if (!url) return;

  event.preventDefault();

  const title = target.dataset.pagePopupTitle || target.textContent?.trim() || '';
  popup.openPage(url, title);
});
